'use client';

import CountrySiteList from '@/components/CountrySiteList';
import Select from '@/components/Form/Select';
import Switch from '@/components/Form/Switch';
import Image from '@/components/Image';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useReporting } from '@/hooks/useReporting';
import { useSpmDomAttr, withSpmCnt } from '@/reporting/spm';
import { getSpmPositionCode, useGlobalSpmTerminal } from '@/components/Layout/spmTerminal';
import { useUserWayWidget } from '@/hooks/useUserWayWidget';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import useEffectWithCurReady from '@/hooks/useEffectWithCurReady';
import { useSiteRedirect } from '@/hooks/useSiteRedirect';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import logger from '@/utils/client-logger';
import { IS_DE_FR } from '@/utils/constants';
import { env } from '@/utils/env';
import { EPlatform } from '@/utils/enums/platform-enum';
import {
  delGoogleTransCookie,
  getNextLocaleCookie,
  setGoogleTransCookie,
  setNextLocaleCookie,
} from '@/utils/locale-cookie';
import { Box, Collapse, MenuItem } from '@mui/material';
import { CollapseArrowUpOutlined, EarthOutlined } from '@zeelool/icon';
import Cookies from 'js-cookie';
import { useTranslations } from '@/i18n/use-translations';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactElement,
  cloneElement,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { getBrowserLanguage } from './browser-language';
import HeaderHoverPopover from './HeaderHoverPopover';
import { TopBarSiteConfigTrigger } from './SiteConfigTrigger';

// 语言选项接口
// 站点设置弹窗名称→坑位按编码表语义固定映射（与线上弹窗选项顺序一致）。
const SITE_SETTINGS_CURRENCY_POSITION: Record<string, string> = {
  USD: 'currency_option_01',
  EUR: 'currency_option_02',
  AUD: 'currency_option_03',
  GBP: 'currency_option_04',
  CAD: 'currency_option_05',
};
const SITE_SETTINGS_LANGUAGE_POSITION: Record<string, string> = {
  Português: 'language_option_01',
  Español: 'language_option_02',
  Deutsch: 'language_option_03',
  Français: 'language_option_04',
  Nederland: 'language_option_05',
  Suomi: 'language_option_06',
  English: 'language_option_07',
};
const SITE_SETTINGS_COUNTRY_POSITION: Record<string, string> = {
  'United Kingdom': 'country_option_001',
  Deutschland: 'country_option_002',
  France: 'country_option_003',
  Canada: 'country_option_004',
};

interface LanguageOption {
  value: string;
  text: string;
  locale: string; // next-intl的locale
}

// 货币选项接口
interface CurrencyOption {
  name: string;
  img_url?: string;
}

// 国家站点接口
interface CountrySite {
  site_name: string;
  m_site_url: string;
  status: number;
}

// 声明gtag全局变量
declare global {
  interface Window {
    gtag: any;
  }
}

const SITE_LANG_MAP = {
  en: 'English',
  ca: 'English',
  uk: 'English',
  fr: 'Français',
  de: 'Deutsch',
};

/**
 * 循环内挂载点 Data SPM DOM 打标：hook 不能在 map 中调用，封为小组件把 data-spm
 * 克隆到唯一真实子元素上（不新增 DOM 层级）；开关关闭或编码表外时原样渲染，零 DOM 差异。
 */
function SpmDomNode({ code, children }: { code: string | undefined; children: ReactElement }) {
  const domSpm = useSpmDomAttr(code);
  return domSpm ? cloneElement(children, domSpm) : children;
}

/**
 * MUI Select 触发器（role=combobox 的 div）挂载 data-spm：data-* 属性运行时合法，
 * 但 @types/react 的 HTMLAttributes 未内置 data-* 索引签名，这里做一次显式收窄。
 */
const asSelectDisplayProps = (domSpm: { 'data-spm': string } | undefined) =>
  domSpm as HTMLAttributes<HTMLDivElement> | undefined;

const SiteConfig = ({
  isDarkMode = false,
  isWeb = false,
  webTriggerVariant = 'navigation',
}: {
  isWeb: boolean;
  isDarkMode?: boolean;
  webTriggerVariant?: 'navigation' | 'topBar';
}) => {
  const router = useRouter();
  const { trackEvent } = useReporting();
  // 站点设置弹窗（site_settings）的坑位编码：仅 WEB 端附加；
  // 名称→坑位按编码表语义固定映射（与线上弹窗选项顺序一致），查表命中才附加。
  // 全局组件 B 位固定 global_pc。
  const spmPage = useGlobalSpmTerminal(EPlatform.WEB);
  const resolveSiteSettingsSpmCnt = (position: string | undefined) =>
    isWeb && position ? getSpmPositionCode(spmPage, 'site_settings', position) : undefined;
  // Data SPM DOM 打标：与各下拉 onOpen 上报的 selector 坑位、各选项点击上报的
  // option 坑位同源同值（查表无该坑位时 undefined，自然不打标）。
  const currencySelectorDomSpm = useSpmDomAttr(resolveSiteSettingsSpmCnt('currency_selector'));
  const countrySelectorDomSpm = useSpmDomAttr(resolveSiteSettingsSpmCnt('country_selector'));
  const languageSelectorDomSpm = useSpmDomAttr(resolveSiteSettingsSpmCnt('language_selector'));
  const { cnName } = useRouteMetadata();
  const { handleSiteRedirectProcess } = useSiteRedirect();
  const contactT = useTranslations('contact-zeelool');
  const t = useTranslations('layout.siteConfig');

  // 从store获取数据 - 使用可选链和默认值
  const currencyList = useStore((state) => state.currencyList);
  const currency = useStore((state) => state.currency);
  const countrySites = useStore((state) => state.countrySiteList);
  const blindMode = useStore((state) => state.waiMode);
  const setBlindMode = useStore((state) => state.setWaiMode);
  const setIsOpenSideNav = useStore((state) => state.setIsOpenSideNav);
  const queryParams = useSearchParams();

  // 状态管理
  const [showCurrency, setShowCurrency] = useState(false);
  const [showLanguage, setShowLanguage] = useState(false);

  const [currentLanguage, setCurrentLanguage] = useState<LanguageOption>({
    value: env.NEXT_PUBLIC_SITE_LANG,
    text: SITE_LANG_MAP[env.NEXT_PUBLIC_SITE_LANG],
    locale: env.NEXT_PUBLIC_SITE_LANG,
  });
  const currencyRef = useRef(currency);
  const currentLanguageRef = useRef(currentLanguage);
  // const [, startTransition] = useTransition();
  const [siteConfigPopoverShow, setSiteConfigPopoverShow] = useState(false);
  const countryWebSiteRef = useRef<string>(env.NEXT_PUBLIC_FULL_COUNTRY_NAME);
  const countryWebSiteFirstMatchedRef = useRef(false);
  const { openUserWayWidget, closeUserWayWidget } = useUserWayWidget();

  // 语言选项 - 映射到next-intl的locale
  const languageList: LanguageOption[] = [
    { value: 'it', text: 'Italiano', locale: 'it' },
    { value: 'pt', text: 'Português', locale: 'pt' },
    { value: 'es', text: 'Español', locale: 'es' },
    { value: 'de', text: 'Deutsch', locale: 'de' },
    { value: 'fr', text: 'Français', locale: 'fr' },
    { value: 'nl', text: 'Nederland', locale: 'nl' },
    { value: 'fi', text: 'Suomi', locale: 'fi' },
    { value: 'en', text: 'English', locale: 'en' },
  ];

  // 语言和币种的映射关系
  const languageCurrencyMap: Record<string, string> = {
    en: env.NEXT_PUBLIC_DEFAULT_CURRENCY,
    fr: 'EUR',
    nl: 'EUR',
    fi: 'EUR',
    es: 'EUR',
    de: 'EUR',
    it: 'EUR',
    pt: 'EUR',
  };

  // 表单配置,WEB端使用的
  const form = useForm({
    defaultValues: {
      language: currentLanguage.value,
      currency: currency?.name || '',
      countryWebsite: env.NEXT_PUBLIC_FULL_COUNTRY_NAME,
    },
  });

  const { control, watch, setValue } = form;

  // 计算属性
  const countrySitesList = useMemo(
    () => countrySites?.filter((item: CountrySite) => item.status === 1) || [],
    [countrySites],
  );

  // 选中当前访问的网站为countryWebsite
  useEffect(() => {
    if (!countryWebSiteFirstMatchedRef.current && countrySitesList?.length) {
      countryWebSiteFirstMatchedRef.current = true;
      const { href } = window.location;
      for (const site of countrySitesList) {
        if (href.includes(site.pc_site_url) || href.includes(site.m_site_url)) {
          // 避免触发事件上报
          countryWebSiteRef.current = site.site_name;
          setValue('countryWebsite', site.site_name, { shouldDirty: false });
          break;
        }
      }
    }
  }, [countrySitesList, setValue]);

  // 处理导航点击埋点
  const handleNavClick = (tabName: string, firstLevelCategory: string) => {
    if (isWeb) {
      return;
    }
    // 移动端和PC的事件名字是隔离的，SideNavBarTab_Click只给移动端使用
    trackEvent(
      {
        eventName: 'SideNavBarTab_Click',
        properties: {
          page_name: cnName,
          url_path: window.location.href,
          tab_name: tabName,
          first_level_category: firstLevelCategory,
        },
      },
      [ProviderType.SENSORS],
    );
  };

  const handleTopNavSelection = (tabName: string) => {
    if (!isWeb || !tabName) return;

    trackEvent(
      {
        eventName: 'TopNavTab_Click',
        properties: {
          tab_name: tabName,
        },
      },
      [ProviderType.SENSORS],
    );
  };

  // 刷新页面，但不带 currency, language 参数
  const reloadPageWithoutCurrencyAndLanguageQuery = () => {
    if (queryParams.get('currency') || queryParams.get('language')) {
      const sp = new URLSearchParams(queryParams);
      sp.delete('currency');
      sp.delete('language');
      const query = sp.size ? `?${sp.toString()}` : '';
      window.location.href = window.location.pathname + query;
    } else {
      window.location.reload();
    }
  };

  // 处理货币切换
  const handleCurrencyChange = (
    currencyItem: CurrencyOption,
    isFromLanguageChange = false,
  ): boolean => {
    logger.info('handleCurrencyChange', currencyItem);
    if (currencyItem.name === currency?.name) return false;
    Cookies.set('zl-currency', currencyItem.name, { expires: 365, path: '/', sameSite: 'lax' });
    handleNavClick('Currency', `Currency_${currencyItem.name}`);
    setShowCurrency(false);
    // isFromLanguageChange == true 时，由语言处理刷新页面的操作
    if (!isFromLanguageChange) {
      // 手动切换币种
      handleSiteRedirectProcess({
        fromChanged: 'currency',
        needSessionFlag: true, // 页面会刷新，需要设置sessionFlag
        language: currentLanguageRef.current.value,
        currency: currencyRef.current.name,
      });
      reloadPageWithoutCurrencyAndLanguageQuery();
      return false;
    }
    return true;
  };

  // 处理语言切换 - 使用基于谷歌翻译，考虑的点如下：
  // 目前公司没有将后端的服务器统一，如果使用next-intl翻译，那么后端的那些字段都无法翻译，就会出现一般小语种一般英语
  // 的情况，所以多语言的能力还是不能直接使用，只能使用谷歌翻译插件
  const handleLanguageChange = async (value: string) => {
    const langItem = languageList.find((item) => item.value === value);
    const text = langItem?.text || '';
    // const targetLocale = langItem?.locale || 'en';
    let needRefresh = false;
    let needRefreshFromLanguage = false;

    try {
      // 使用setUserLocale设置cookie，这段代码验证过时可以直接切换语言的，现在注释掉
      // startTransition(async () => {
      //   await setUserLocale(targetLocale as any);

      //   // 根据语言自动匹配币种 - 在语言切换后处理
      //   const targetCurrency = languageCurrencyMap[value];
      //   if (targetCurrency) {
      //     const currencyObj = currencyList?.find((c: CurrencyOption) => c.name === targetCurrency);
      //     if (currencyObj && currencyObj.name !== currency?.name && setCurrency) {
      //       handleCurrencyChange(currencyObj, true);
      //     }
      //   }

      //   // 更新当前语言状态
      //   setCurrentLanguage(langItem || { value: 'en', text: 'English', locale: 'en' });
      //   localStorage.setItem('zl-language', text);
      //   localStorage.setItem('zl-locale', targetLocale);

      //   // 如果切换到英语，需要刷新页面
      //   if (value === 'en') {
      //     setTimeout(() => {
      //       // 获取主域名
      //       const mainDomain = `.${window.location.hostname.split('.').slice(-2).join('.')}`;

      //       // 删除所有相关域名的 cookie
      //       Cookies.remove(LANG_COOKIE_NAME);
      //       Cookies.remove(LANG_COOKIE_NAME, { domain: mainDomain, path: '/' });

      //       // 检查 URL 中是否有语言参数
      //       const currentQuery: Record<string, string> = {};
      //       queryParams.forEach((v, key) => {
      //         currentQuery[key] = v;
      //       });
      //       if (currentQuery.language) {
      //         delete currentQuery.language;
      //         // 构建新的 URL
      //         const path = window.location.pathname;
      //         const queryString = Object.keys(currentQuery).length
      //           ? `?${Object.entries(currentQuery)
      //               .map(([key, val]) => `${key}=${val}`)
      //               .join('&')}`
      //           : '';
      //         window.location.href = `${path}${queryString}`;
      //       } else {
      //         location.reload();
      //       }
      //     }, 1000);
      //   }
      // });

      // 根据语言自动匹配币种
      const targetCurrency = languageCurrencyMap[value];
      if (targetCurrency) {
        // 找到对应的币种对象
        const currencyObj = currencyList.find((c) => c.name === targetCurrency);
        if (currencyObj && currencyObj.name !== currency?.name) {
          // 如果找到对应币种且与当前币种不同，则自动切换
          needRefreshFromLanguage = handleCurrencyChange(currencyObj, true);
        }
      }

      localStorage.setItem('zl-language', text);
      localStorage.setItem('zl-locale', langItem?.locale || 'en');
      const select = document.querySelector('.goog-te-combo');
      if (select) {
        (select as HTMLSelectElement).value = value;
        (select as HTMLSelectElement).dispatchEvent(new Event('change'));
        setNextLocaleCookie(value);
      } else {
        // 重新设置 cookie
        setGoogleTransCookie(value);
      }

      setCurrentLanguage(langItem || { value: 'en', text: 'English', locale: 'en' });

      // 如果切换到英语，需要刷新页面
      if (value === 'en') {
        needRefresh = true;
        setTimeout(() => {
          // 删除所有相关域名的 cookie
          delGoogleTransCookie();
          reloadPageWithoutCurrencyAndLanguageQuery();
        }, 1000);
      } else if (needRefreshFromLanguage) {
        setTimeout(() => reloadPageWithoutCurrencyAndLanguageQuery(), 1000);
      }
    } catch (error) {
      logger.error('Language change error:', error);
    } finally {
      handleNavClick('Language', `Language_${text}`);
      setShowLanguage(false);
    }
    // 手动设置语言
    setTimeout(() =>
      handleSiteRedirectProcess({
        fromChanged: 'language',
        needSessionFlag: needRefresh || needRefreshFromLanguage,
        language: currentLanguageRef.current.value,
        currency: currencyRef.current.name,
      }),
    );
  };

  // 处理无障碍模式切换
  const handleBlindModeChange = (checked: boolean) => {
    if (setBlindMode) {
      setBlindMode(checked);
    }
    if (checked) {
      openUserWayWidget();
    } else {
      closeUserWayWidget();
    }
    handleNavClick('Accessibility', `Accessibility_${checked ? 'Open' : 'Close'}`);
  };

  // 处理国家站点切换
  const handleCountrySiteChange = (siteName: string) => {
    handleNavClick('Country Website', `Country Website_${siteName}`);
    // setShowCountrySites(false);
  };

  // 跳转到联系我们页面
  const goToContactUs = () => {
    // TODO: 设置注册来源信息
    setIsOpenSideNav(false);
    router.push('/contact-zeelool');
  };

  // 监听表单值变化
  const watchedLanguage = watch('language');
  const watchedCurrency = watch('currency');
  const watchedCountryWebsite = watch('countryWebsite');

  // 和 countrySitesList 不一样，没有当前选择的国家
  const countrySiteSelectList = useMemo(
    () => countrySitesList.filter((item) => item.site_name !== watchedCountryWebsite),
    [countrySitesList, watchedCountryWebsite],
  );

  // 处理语言变化
  useEffect(() => {
    if (watchedLanguage && watchedLanguage !== currentLanguage.value) {
      const buttonName = languageList.find((it) => it.value === watchedLanguage)?.text;

      if (isWeb) {
        trackEvent(
          {
            eventName: 'SetPopUp_Click',
            properties: (() => {
              const properties = {
                page_name: cnName,
                url_path: window.location.href,
                btn_name: `Language_${buttonName}`,
              };
              const spmCnt = resolveSiteSettingsSpmCnt(
                buttonName ? SITE_SETTINGS_LANGUAGE_POSITION[buttonName] : undefined,
              );
              return spmCnt ? withSpmCnt(properties, spmCnt) : properties;
            })(),
          },
          [ProviderType.SENSORS],
        );
      }
      handleLanguageChange(watchedLanguage);
    }
  }, [watchedLanguage]);

  // 处理货币变化
  useEffect(() => {
    if (watchedCurrency && watchedCurrency !== currency?.name) {
      if (isWeb) {
        trackEvent(
          {
            eventName: 'SetPopUp_Click',
            properties: (() => {
              const properties = {
                page_name: cnName,
                url_path: window.location.href,
                btn_name: `Currency_${watchedCurrency}`,
              };
              const spmCnt = resolveSiteSettingsSpmCnt(
                watchedCurrency ? SITE_SETTINGS_CURRENCY_POSITION[watchedCurrency] : undefined,
              );
              return spmCnt ? withSpmCnt(properties, spmCnt) : properties;
            })(),
          },
          [ProviderType.SENSORS],
        );
      }
      const currencyItem = currencyList?.find((item) => item.name === watchedCurrency);
      if (currencyItem) {
        handleCurrencyChange(currencyItem);
      }
    }
  }, [watchedCurrency]);

  // 处理国家网站变化
  useEffect(() => {
    logger.info('watchedCountryWebsite', watchedCountryWebsite, countryWebSiteRef.current);
    if (watchedCountryWebsite && watchedCountryWebsite !== countryWebSiteRef.current) {
      countryWebSiteRef.current = watchedCountryWebsite;
      if (isWeb) {
        trackEvent(
          {
            eventName: 'SetPopUp_Click',
            properties: (() => {
              const properties = {
                page_name: cnName,
                url_path: window.location.href,
                btn_name: `Country Website_${watchedCountryWebsite}`,
              };
              const spmCnt = resolveSiteSettingsSpmCnt(
                watchedCountryWebsite
                  ? SITE_SETTINGS_COUNTRY_POSITION[watchedCountryWebsite]
                  : undefined,
              );
              return spmCnt ? withSpmCnt(properties, spmCnt) : properties;
            })(),
          },
          [ProviderType.SENSORS],
        );
      }
      handleCountrySiteChange(watchedCountryWebsite);
    }
  }, [watchedCountryWebsite]);

  // 当外部状态变化时更新表单值
  useEffect(() => {
    setValue('language', currentLanguage.value);
  }, [currentLanguage.value, setValue]);

  useEffect(() => {
    if (currentLanguage) {
      currentLanguageRef.current = currentLanguage;
    }
  }, [currentLanguage]);

  useEffect(() => {
    if (currency) {
      currencyRef.current = currency;
    }
  }, [currency]);

  useEffect(() => {
    setValue('currency', currency?.name || '');
  }, [currency?.name, setValue]);

  // 对齐谷歌翻译插件语言和页面选择的语言
  // 匹配优先级：广告参数＞手动
  const alignmentLanguage = useCallback(() => {
    const transTarget = getNextLocaleCookie() || 'en';
    if (
      ((!transTarget || transTarget === 'en') && currentLanguage.value === 'en') ||
      transTarget === currentLanguage.value
    ) {
      // 用户未选择语言或选择的语言和当前语言相同
      return;
    }
    if (currentLanguage.value === 'en' && !(transTarget === 'en' || !transTarget)) {
      // 当前语言不为英文
      currentLanguage.value = transTarget;
      return;
    }
    if (currentLanguage.value !== 'en') {
      // 用户在谷歌插件加载前选择了语言
      const select = document.querySelector('.goog-te-combo');
      if (select) {
        (select as HTMLSelectElement).value = currentLanguage.value;
        (select as HTMLSelectElement).dispatchEvent(new Event('change'));
        setNextLocaleCookie(currentLanguage.value);
      } else {
        setGoogleTransCookie(currentLanguage.value);
      }
    }
  }, [currentLanguage.value]);

  // 初始化语言设置
  // 1. 从url参数中获取语言
  // 2. 从谷歌翻译插件获取语言
  useEffect(() => {
    try {
      // 从localStorage获取之前选择的语言
      const savedLocale = localStorage.getItem('zl-locale');
      const savedLanguage = localStorage.getItem('zl-language');

      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('language')) {
        const langItem = languageList.find((l) => l.value === urlParams.get('language'));
        if (langItem) {
          delGoogleTransCookie();
          setCurrentLanguage(langItem);
          localStorage.setItem('zl-language', langItem.text);
          localStorage.setItem('zl-locale', langItem.locale);
        }
      } else if (savedLocale && savedLanguage) {
        const langItem = languageList.find((l) => l.locale === savedLocale);
        if (langItem) {
          setCurrentLanguage(langItem);
        }
      } else {
        // 从cookie中获取当前语言
        const localeValue = getNextLocaleCookie();
        if (localeValue) {
          const langItem = languageList.find((l) => l.locale === localeValue);
          if (langItem) {
            setCurrentLanguage(langItem);
          }
        } else {
          const targetLanguage = getBrowserLanguage();
          const langItem = targetLanguage
            ? languageList.find((l) => l.value === targetLanguage)
            : undefined;
          if (langItem && langItem.value !== currentLanguageRef.current.value) {
            setTimeout(() => handleLanguageChange(langItem.value));
          }
        }
      }
    } catch (error) {
      logger.error('Language initialization error:', error);
    }

    // 注意：旧版的在此之前执行了一次 alignmentLanguage，新版这里没有这一步
    // 路由上的语言和币种设置完毕
    const timer = setTimeout(() =>
      handleSiteRedirectProcess({
        isInit: true,
        language: currentLanguageRef.current.value,
        currency: currencyRef.current.name,
      }),
    );
    // 如果存在页面翻译显示自定义语言选择框
    const translateElement = document.getElementById('google_translate_element');
    if (translateElement) {
      // 页面翻译组件加载完毕
      translateElement.addEventListener('googleTranslateLoaded', alignmentLanguage);
    }
    return () => clearTimeout(timer);
  }, []);

  useEffectWithCurReady(() => {
    // 若路由上执行的函数时，currencyReady未就绪，则实际函数不会执行
    // 因此currencyReady就绪时，需要重新执行一次
    // 函数内部会确保isInit只执行一次
    handleSiteRedirectProcess({
      isInit: true,
      language: currentLanguageRef.current.value,
      currency: currencyRef.current.name,
    });
  }, [currentLanguage.value, handleSiteRedirectProcess]);

  useEffect(() => {
    if (siteConfigPopoverShow) {
      trackEvent(
        {
          eventName: 'SetPopUp_Exp',
          properties: {
            page_name: cnName,
            url_path: window.location.href,
          },
        },
        [ProviderType.SENSORS],
      );
    }
  }, [siteConfigPopoverShow]);

  const siteConfigSummary = `${currency?.name || env.NEXT_PUBLIC_DEFAULT_CURRENCY} · ${env.NEXT_PUBLIC_SITE_NAME.toUpperCase()} · ${currentLanguage.text}`;

  return (
    <>
      <WapOnly>
        <div>
          {/* 客服信息 */}
          <section className="bg-[#fafafa] px-[14px] py-[30px]">
            {env.NEXT_PUBLIC_FOOTER_SHOW_TEL === 'true' && (
              <div className="mb-[20px]">
                <a
                  href={`tel:${contactT('contactList.phoneNumber')}`}
                  className="text-[14px] font-bb text-[#282828] block mb-1"
                >
                  {contactT('contactList.phoneNumber')}
                </a>
                <p className="text-[12px] text-[#a9a9a9] leading-[15px]">
                  {env.NEXT_PUBLIC_CUSTOMER_SERVICE_TIME}
                </p>
              </div>
            )}
            <div className="mb-[20px]">
              <p className="text-[14px] font-bb text-[#282828]">{env.NEXT_PUBLIC_MARKET_EMAIL}</p>
              <p className="text-[12px] text-[#a9a9a9] mt-1">{t('businessCooperation')}</p>
            </div>
            <div>
              <p
                className="text-[14px] font-bb text-[#282828] cursor-pointer"
                onClick={goToContactUs}
              >
                {t('contactUs')}
              </p>
            </div>
          </section>

          {/* 语言翻译选择 */}
          {!IS_DE_FR ? (
            <section>
              <div
                className="flex justify-between items-center px-[14px] h-[60px] bg-[#fafafa] cursor-pointer"
                onClick={() => {
                  setShowLanguage(!showLanguage);
                  handleNavClick('Language', `Language_${currentLanguage.text}`);
                }}
              >
                <span className="text-[14px] font-bb">{t('language')}</span>
                <div className="flex items-center">
                  <span className="text-[14px] text-[#a9a9a9] notranslate mr-2">
                    {currentLanguage.text}
                  </span>
                  <CollapseArrowUpOutlined
                    className={`text-[12px] transition-all duration-300 ${
                      showLanguage ? 'rotate-180' : 'rotate-[90deg]'
                    }`}
                    aria-hidden
                  />
                </div>
              </div>
              <Collapse in={showLanguage} timeout="auto" unmountOnExit>
                <div className="sec-item translateSelect bg-[#fafafa] px-[14px] py-[14px]">
                  <select
                    value={currentLanguage.value}
                    className="language-select notranslate w-full p-2 border border-gray-300 rounded"
                    onChange={(e) => handleLanguageChange(e.target.value)}
                  >
                    {languageList.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.text}
                      </option>
                    ))}
                  </select>
                </div>
              </Collapse>
            </section>
          ) : null}

          {/* 货币选择 */}
          {!IS_DE_FR ? (
            <section className="currency-box mb-[7px]">
              <div
                className="title flex justify-between items-center px-[14px] h-[60px] bg-[#fafafa] cursor-pointer"
                onClick={() => {
                  setShowCurrency(!showCurrency);
                  handleNavClick('Currency', `Currency_${currency?.name || ''}`);
                }}
              >
                <span className="text-[14px] font-bb">{t('currency')}</span>
                <div className="flex items-center">
                  <span className="text-[14px] text-[#a9a9a9] notranslate mr-2">
                    {currency?.name}
                  </span>
                  <CollapseArrowUpOutlined
                    className={`text-[12px] transition-all duration-300 ${
                      showCurrency ? 'rotate-180' : 'rotate-[90deg]'
                    }`}
                    aria-hidden
                  />
                </div>
              </div>
              <Collapse in={showCurrency} timeout="auto" unmountOnExit>
                <ul className="currency bg-[#fafafa] px-[14px]">
                  {currencyList?.map((item: CurrencyOption, index: number) => (
                    <li
                      key={index}
                      className="flex items-center h-[35px] cursor-pointer"
                      onClick={() => handleCurrencyChange(item)}
                    >
                      <div className="w-[16px] mr-[12px]">
                        {item.img_url && (
                          <Image
                            className="w-[16px] block"
                            src={item.img_url}
                            alt=""
                            width={56}
                            height={56}
                          />
                        )}
                      </div>
                      <div className="text-[14px] text-[#282828]">{item.name}</div>
                    </li>
                  ))}
                </ul>
              </Collapse>
            </section>
          ) : null}

          {/* 无障碍模式 */}
          <section>
            <div className="title flex justify-between items-center px-[14px] h-[60px]">
              <div className="text-[14px] font-bb">{t('accessibility')}</div>
              <div>
                <Switch
                  checked={blindMode}
                  onChange={(event, checked) => handleBlindModeChange(checked)}
                  aria-label={t('enableAccessibility')}
                  width={42}
                  height={22}
                />
              </div>
            </div>
          </section>

          {/* 国家站跳转 */}
          <section className="px-14">
            <CountrySiteList
              isRegionLabelRegular
              onCountryClick={(shortName) => handleNavClick('Region', `Region_${shortName}`)}
              deviceType={EPlatform.WAP}
            />
          </section>
        </div>
      </WapOnly>
      <WebOnly>
        <HeaderHoverPopover
          id="site-config-popover"
          interactionMode={webTriggerVariant === 'topBar' ? 'hover-only' : 'hover-focus'}
          triggerComponent={
            webTriggerVariant === 'topBar' ? (
              <TopBarSiteConfigTrigger
                summary={siteConfigSummary}
                flagUrl={currency?.img_url}
                ariaLabel={t('siteSettings')}
              />
            ) : (
              <button
                type="button"
                className="pt-0 pr-0 ml-0 mr-0 mt-0 border-0 bg-transparent pb-[14px] pl-[24px] -mb-[14px] cursor-pointer block"
                aria-label={t('siteSettings')}
              >
                {['uk', 'ca'].includes(env.NEXT_PUBLIC_SITE_NAME) ? (
                  <Image
                    src={currency?.img_url}
                    alt={env.NEXT_PUBLIC_SITE_NAME}
                    width={24}
                    height={24}
                    className={`text-[24px] ${isDarkMode ? 'text-white' : 'text-primary-black'}`}
                  />
                ) : (
                  <EarthOutlined
                    className={`text-[24px] ${isDarkMode ? 'text-white' : 'text-primary-black'}`}
                    aria-hidden
                  />
                )}
              </button>
            )
          }
          width={270}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          slotProps={{
            transition: {
              onEnter: () => {
                setSiteConfigPopoverShow(true);
              },
              onExited: () => {
                setSiteConfigPopoverShow(false);
              },
            },
          }}
          // open
          content={
            <FormProvider {...form}>
              {env.NEXT_PUBLIC_SITE_NAME === 'us' && (
                <Box>
                  <div className="mb-[10px] text-[16px] font-bb text-[#808080]">
                    {t('currency')}
                  </div>
                  <Select
                    name="currency"
                    control={control}
                    displayEmpty
                    className="bg-gray-50"
                    size="small"
                    fullWidth
                    formItemClassName="mb-[10px]"
                    SelectDisplayProps={asSelectDisplayProps(currencySelectorDomSpm)}
                    onChange={(event) => {
                      const selectedCurrency = String(event.target.value || '');
                      if (selectedCurrency !== currency?.name) {
                        handleTopNavSelection(selectedCurrency);
                      }
                    }}
                    onOpen={() => {
                      trackEvent(
                        {
                          eventName: 'SetPopUp_Click',
                          properties: (() => {
                            const properties = {
                              page_name: cnName,
                              url_path: window.location.href,
                              btn_name: `Currency_${watchedCurrency}`,
                            };
                            const spmCnt = resolveSiteSettingsSpmCnt('currency_selector');
                            return spmCnt ? withSpmCnt(properties, spmCnt) : properties;
                          })(),
                        },
                        [ProviderType.SENSORS],
                      );
                    }}
                    renderValue={(value: unknown) => {
                      const selectedCurrency = currencyList.find((item) => item.name === value);
                      return (
                        <div className="flex items-center notranslate">
                          {selectedCurrency?.img_url && (
                            <Image
                              src={selectedCurrency.img_url}
                              alt={selectedCurrency.name}
                              width={16}
                              height={16}
                              className="mr-8"
                            />
                          )}
                          {selectedCurrency?.name}
                        </div>
                      );
                    }}
                  >
                    {currencyList?.map((item) => (
                      <SpmDomNode
                        key={item.name}
                        code={resolveSiteSettingsSpmCnt(SITE_SETTINGS_CURRENCY_POSITION[item.name])}
                      >
                        <MenuItem value={item.name} className="notranslate">
                          {item.img_url && (
                            <Image
                              src={item.img_url}
                              alt={item.name}
                              width={16}
                              height={16}
                              className="mr-8"
                            />
                          )}
                          {item.name}
                        </MenuItem>
                      </SpmDomNode>
                    ))}
                  </Select>
                </Box>
              )}
              {countrySiteSelectList.length > 0 && (
                <Box>
                  <div className="my-[10px] text-[16px] font-bb text-[#808080]">
                    {t('countryWebsite')}
                  </div>
                  <Select
                    name="countryWebsite"
                    control={control}
                    // displayEmpty
                    className="bg-gray-50 notranslate"
                    size="small"
                    fullWidth
                    formItemClassName="mb-[10px]"
                    SelectDisplayProps={asSelectDisplayProps(countrySelectorDomSpm)}
                    onChange={(event) => {
                      const selectedCountry = String(event.target.value || '');
                      if (selectedCountry !== countryWebSiteRef.current) {
                        handleTopNavSelection(selectedCountry);
                      }
                    }}
                    onOpen={() => {
                      trackEvent(
                        {
                          eventName: 'SetPopUp_Click',
                          properties: (() => {
                            const properties = {
                              page_name: cnName,
                              url_path: window.location.href,
                              btn_name: `Country Website_${watchedCountryWebsite}`,
                            };
                            const spmCnt = resolveSiteSettingsSpmCnt('country_selector');
                            return spmCnt ? withSpmCnt(properties, spmCnt) : properties;
                          })(),
                        },
                        [ProviderType.SENSORS],
                      );
                    }}
                  >
                    {countrySiteSelectList.map((item) => (
                      <SpmDomNode
                        key={item.site_name}
                        code={resolveSiteSettingsSpmCnt(
                          SITE_SETTINGS_COUNTRY_POSITION[item.site_name],
                        )}
                      >
                        <MenuItem
                          value={item.site_name}
                          className="notranslate"
                          onClick={() => {
                            trackEvent(
                              {
                                eventName: 'SetPopUp_Click',
                                properties: {
                                  page_name: cnName,
                                  url_path: window.location.href,
                                  btn_name: `Country Website_${item.site_name}`,
                                },
                              },
                              [ProviderType.SENSORS],
                            );
                            const targetUrl = item.pc_site_url;
                            if (targetUrl) {
                              if (!location.href.includes(targetUrl)) {
                                setTimeout(() => window.open(targetUrl, '_self'), 200);
                              }
                            }
                          }}
                        >
                          {item.site_name}
                        </MenuItem>
                      </SpmDomNode>
                    ))}
                  </Select>
                </Box>
              )}
              {!IS_DE_FR && (
                <Box>
                  <div className="my-[10px] text-[16px] font-bb text-[#808080]">
                    {t('language')}
                  </div>
                  <Select
                    name="language"
                    control={control}
                    displayEmpty
                    className="bg-gray-50"
                    size="small"
                    fullWidth
                    formItemClassName="mb-[10px]"
                    SelectDisplayProps={asSelectDisplayProps(languageSelectorDomSpm)}
                    onChange={(event) => {
                      const selectedLanguage = languageList.find(
                        (item) => item.value === event.target.value,
                      );
                      if (selectedLanguage?.value !== currentLanguage.value) {
                        handleTopNavSelection(selectedLanguage?.text || '');
                      }
                    }}
                    onOpen={() => {
                      const buttonName = languageList.find(
                        (it) => it.value === watchedLanguage,
                      )?.text;
                      trackEvent(
                        {
                          eventName: 'SetPopUp_Click',
                          properties: (() => {
                            const properties = {
                              page_name: cnName,
                              url_path: window.location.href,
                              btn_name: `Language_${buttonName}`,
                            };
                            const spmCnt = resolveSiteSettingsSpmCnt('language_selector');
                            return spmCnt ? withSpmCnt(properties, spmCnt) : properties;
                          })(),
                        },
                        [ProviderType.SENSORS],
                      );
                    }}
                    renderValue={(value: unknown) => {
                      const selectedLanguage = languageList.find((item) => item.value === value);
                      return selectedLanguage?.text || (value as string);
                    }}
                  >
                    {languageList.map((item) => (
                      <SpmDomNode
                        key={item.value}
                        code={resolveSiteSettingsSpmCnt(SITE_SETTINGS_LANGUAGE_POSITION[item.text])}
                      >
                        <MenuItem value={item.value} className="notranslate">
                          {item.text}
                        </MenuItem>
                      </SpmDomNode>
                    ))}
                  </Select>
                </Box>
              )}
            </FormProvider>
          }
        />
      </WebOnly>
    </>
  );
};

export const TopBarSiteConfig = () => <SiteConfig isWeb webTriggerVariant="topBar" />;

export default SiteConfig;
